import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/api";
import { ChefHat, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Bem-vindo ao MenuPro");
      navigate("/");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 text-white flex-col justify-between p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-emerald-800 flex items-center justify-center">
              <ChefHat size={22} />
            </div>
            <div className="text-2xl font-semibold tracking-tight">MenuPro</div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-300 mb-3">Alimentação Industrial</div>
          <h2 className="text-3xl font-semibold leading-tight mb-4">Gestão inteligente de cardápios e compras.</h2>
          <p className="text-slate-300 text-sm leading-relaxed">
            Cadastre empresas, monte cardápios semanais com duas opções de proteína por dia
            e receba automaticamente a lista de compras — por empresa ou consolidada para negociar com distribuidoras.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { k: "18+", v: "empresas atendidas" },
              { k: "2/dia", v: "opções de proteína" },
              { k: "kg", v: "cálculo automático" },
            ].map((s) => (
              <div key={s.k} className="border-l-2 border-emerald-700 pl-3">
                <div className="text-2xl font-semibold tabular-nums">{s.k}</div>
                <div className="text-xs text-slate-400 uppercase tracking-wide">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500">© 2026 MenuPro · Uso profissional</div>
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1589109807644-924edf14ee09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBwcm9mZXNzaW9uYWwlMjBpbmR1c3RyaWFsJTIwa2l0Y2hlbnxlbnwwfHx8fDE3ODQ4MjY3NDN8MA&ixlib=rb-4.1.0&q=85')" }}
        />
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-md bg-emerald-800 flex items-center justify-center text-white"><ChefHat size={20} /></div>
            <div className="text-xl font-semibold">MenuPro</div>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Entrar</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">Acesse sua conta MenuPro.</p>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wide text-slate-600">Email</Label>
              <Input
                id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input" className="mt-1 bg-white" autoComplete="email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wide text-slate-600">Senha</Label>
              <Input
                id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input" className="mt-1 bg-white" autoComplete="current-password"
              />
            </div>
            <Button
              type="submit" disabled={busy}
              data-testid="login-submit-btn"
              className="w-full bg-emerald-900 hover:bg-emerald-800 text-white transition-colors"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Entrar
            </Button>
          </form>
          <div className="text-sm text-slate-500 mt-5">
            Ainda não tem uma conta?{" "}
            <Link to="/register" className="text-emerald-800 font-medium hover:underline" data-testid="go-register-link">
              Criar conta
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
